// #ux-redesign-start: StudiesList rediseñada (Patients).
// Reemplaza <Table> manual + paginador legacy por <DataTable> del DS con
// paginación moderna. Mobile-first:
//   - Toolbar: search + date filter en columna única en xs; en md+ una sola fila.
//   - Las columnas Doctor / Status / Type se muestran como ribbon en el row de
//     mobile (el DataTable colapsa columnas extras via CSS responsive cuando el
//     viewport no entra).
// Acciones: Detalles (link) + ícono PDF + ícono "nuevo review".
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from 'react-translate';
import moment from 'moment';
import history from 'historyRouter';
import Icon from 'components/Icons/Icon';
import useResponsive from 'hooks/useResponsive';

import {
  Input,
  DataTable,
  Button,
  Tooltip,
  LoadingBlock,
  EmptyState,
  Badge,
  toast,
} from 'components/ds';
import PdfLoadingButton from 'components/Common/Laboratory/LaboratoryPdfDownload';

import {
  actionGetLabStudies,
  actionSetSearchQuery,
  actionSaveCurrentLaborScroll,
  actionSetFromDetailsLabor,
  actionSetPageIndex,
} from 'store/actions/laboratoryActions';

const CopyIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const FlaskIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 3h6v5l4.5 9a2 2 0 0 1-1.79 3H5.29a2 2 0 0 1-1.79-3L8 8z" />
    <line x1="9" y1="3" x2="15" y2="3" />
  </svg>
);

const PdfIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const DATE_FILTER_OPTIONS = [
  { value: 'all',         labelKey: 'all-dates' },
  { value: 'last-7-days', labelKey: 'last-7-days' },
  { value: 'last-month',  labelKey: 'last-month' },
  { value: 'personalized', labelKey: 'personalized' },
];

const StudiesList = () => {
  const dispatch = useDispatch();
  const t = useTranslate('studies-list');
  const tDetail = useTranslate('laboratory-detail');
  const thl7 = useTranslate('hl7-status');
  const ta = useTranslate('a11y');
  const { isMobile } = useResponsive();

  const laboratoryList = useSelector((s) => s.laboratoryReducer.studies);
  const pageLimit = useSelector((s) => s.laboratoryReducer.limit);
  const pageState = useSelector((s) => s.laboratoryReducer.page);
  const totalEntries = useSelector((s) => s.laboratoryReducer.totalEntries);
  const loadingStudyList = useSelector((s) => s.laboratoryReducer.loadingStudyList);
  const querySearch = useSelector((s) => s.laboratoryReducer.querySearch);
  const coming = useSelector((s) => s.laboratoryReducer.comingFromDetails);
  const scroll = useSelector((s) => s.laboratoryReducer.laborListScroll);

  const refreshTable = useRef(false);
  const [sortDate, setSortDate] = useState(-1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateMode, setDateMode] = useState('all');
  const [searchInput, setSearchInput] = useState(querySearch || '');

  // Debounce search → Redux + reset page
  useEffect(() => {
    const id = setTimeout(() => {
      if (searchInput.trim() !== querySearch) {
        dispatch(actionSetSearchQuery(searchInput.trim()));
        dispatch(actionSetPageIndex(0));
      }
    }, 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Fetch al cambiar page / filtros
  useEffect(() => {
    refreshTable.current = true;
    dispatch(
      actionGetLabStudies({
        pageIndex: pageState,
        pageSize: pageLimit,
        sortDate,
        querySearch,
        dateFrom,
        dateTo,
      })
    );
  }, [pageState, pageLimit, sortDate, querySearch, dateFrom, dateTo, dispatch]);

  // Restaurar scroll al volver de detalle
  useEffect(() => {
    if (!loadingStudyList && laboratoryList && laboratoryList.length && scroll && coming) {
      const fp = document.getElementById('full-page');
      if (fp) fp.scrollTop = scroll;
      dispatch(actionSaveCurrentLaborScroll(0));
      dispatch(actionSetFromDetailsLabor(false));
    }
  }, [laboratoryList]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDateMode = (value) => {
    setDateMode(value);
    const today = moment().endOf('day');
    if (value === 'all') {
      setDateFrom(''); setDateTo('');
    } else if (value === 'last-7-days') {
      setDateFrom(moment().subtract(7, 'days').startOf('day').format('YYYY-MM-DD'));
      setDateTo(today.format('YYYY-MM-DD'));
    } else if (value === 'last-month') {
      setDateFrom(moment().subtract(1, 'month').startOf('day').format('YYYY-MM-DD'));
      setDateTo(today.format('YYYY-MM-DD'));
    }
  };

  const goToDetail = (p) => {
    dispatch(actionSaveCurrentLaborScroll(
      (document.getElementById('full-page') || {}).scrollTop || 0
    ));
    dispatch(actionSetFromDetailsLabor(true));
    history.push(`lab-studies-detail/${p?._id}`);
  };

  const columns = useMemo(() => [
    {
      Header: tDetail('order') || 'ID',
      accessor: 'reportid',
      disableSortBy: true,
    },
    {
      Header: tDetail('study-type'),
      accessor: 'studyType',
      disableSortBy: true,
    },
    {
      Header: tDetail('reception-date'),
      accessor: 'eingangsdatum',
      defaultCanSort: true,
      sortType: (a, b) => (a.original._raw_date || 0) - (b.original._raw_date || 0),
    },
    {
      Header: tDetail('doctor'),
      accessor: 'arzt',
      disableSortBy: true,
    },
    {
      Header: tDetail('status'),
      accessor: 'status',
      defaultCanSort: true,
    },
    {
      Header: 'PDF',
      accessor: 'pdfCell',
      disableSortBy: true,
      id: 'pdf',
    },
    {
      Header: '',
      accessor: 'actionsCell',
      disableSortBy: true,
      id: 'actions',
    },
  ], [tDetail]);

  const data = useMemo(() => {
    const list = Array.isArray(laboratoryList) ? laboratoryList : [];
    return list.map((p) => ({
      _raw_date: p?.created_at ? Number(moment(p.created_at, 'YYYYMMDDhhmmss').format('x')) : 0,
      eingangsdatum: p?.created_at
        ? moment(p.created_at, 'YYYYMMDDhhmmss').format('DD/MM/YYYY')
        : '—',
      status: p?.status ? thl7(p.status) : '—',
      studyType: p.studyType
        ? tDetail(p.studyType.toLowerCase().replace(' ', '-'))
        : '—',
      arzt: (
        <span className="ds-studies__doctor">
          {p.doctor?.title && (
            <small className="ds-studies__doctor-title">{p.doctor.title}</small>
          )}
          <span>{p.doctor?.lastName}</span>
        </span>
      ),
      reportid: (
        <span className="ds-studies__order">
          <span className="ds-studies__order-id">{p.reportId}</span>
          {p.reportId != null && p.reportId !== '' && (
            <Tooltip content={t('copy-order')} side="top">
              <button
                type="button"
                className="ds-studies__copy-btn"
                aria-label={t('copy-order')}
                onClick={(e) => {
                  e.stopPropagation();
                  const value = String(p.reportId);
                  const done = () => toast.success(t('order-copied'));
                  if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(value).then(done).catch(() => {});
                  } else {
                    const ta = document.createElement('textarea');
                    ta.value = value;
                    document.body.appendChild(ta);
                    ta.select();
                    try { document.execCommand('copy'); done(); } catch (_) { /* noop */ }
                    document.body.removeChild(ta);
                  }
                }}
              >
                <CopyIcon />
              </button>
            </Tooltip>
          )}
        </span>
      ),
      pdfCell: p.reportFileExists ? (
        <span onClick={(e) => e.stopPropagation()}>
          <PdfLoadingButton
            reportId={p._id}
            doctorId={p.doctor?.auth_user_id}
            patientId={p.patient?.auth_user_id}
            prompt={<i className="icon icon-attach" />}
            buttonSize="sm"
          />
        </span>
      ) : null,
      actionsCell: (
        <span className="ds-studies__actions-cell">
          {p?.new_review && (
            <Tooltip content={t('new-doctor-review') || 'New review'} side="bottom">
              <span
                className="ds-studies__new-review"
                onClick={(e) => {
                  e.stopPropagation();
                  history.push(`lab-studies-detail/${p?._id}/review`);
                }}
              >
                <Icon file="medical-report_TP.svg" width="22px" />
              </span>
            </Tooltip>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              goToDetail(p);
            }}
          >
            {t('details')}
          </Button>
        </span>
      ),
      _unread: !p?.readed,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laboratoryList, t, tDetail, thl7]);

  const pageCount = Math.max(
    1,
    Math.ceil(totalEntries / (pageLimit || 10))
  );

  // Filas crudas para la versión mobile (cards). Reutiliza la lista del store y
  // mapea sólo lo que la card necesita; no depende del shape de DataTable.
  const mobileRows = useMemo(() => {
    const list = Array.isArray(laboratoryList) ? laboratoryList : [];
    return list.map((p) => ({
      _raw: p,
      id: p._id,
      reportId: p.reportId,
      dateShort: p?.created_at
        ? moment(p.created_at, 'YYYYMMDDhhmmss').format('MM/YY')
        : '—',
      dateFull: p?.created_at
        ? moment(p.created_at, 'YYYYMMDDhhmmss').format('DD/MM/YYYY')
        : '—',
      doctorTitle: p.doctor?.title || '',
      doctorName: p.doctor?.lastName || '',
      doctorId: p.doctor?.auth_user_id,
      patientId: p.patient?.auth_user_id,
      status: p?.status ? thl7(p.status) : '',
      statusKey: p?.status || '',
      studyType: p.studyType
        ? tDetail(p.studyType.toLowerCase().replace(' ', '-'))
        : '',
      unread: !p?.readed,
      newReview: !!p?.new_review,
      hasPdf: !!p?.reportFileExists,
    }));
  }, [laboratoryList, tDetail, thl7]);

  const showSimplePagination = isMobile && totalEntries > 0;
  const lastPageIdx = Math.max(0, pageCount - 1);

  // Status → variant de Badge para tono visual.
  const statusVariant = (key) => {
    const k = (key || '').toLowerCase();
    if (k.includes('open') || k.includes('draft') || k.includes('pending')) return 'warning';
    if (k.includes('readed') || k.includes('read')) return 'success';
    if (k.includes('sent') || k.includes('delivered')) return 'info';
    return 'neutral';
  };

  const renderMobileCard = (row) => (
    <article
      key={row.id}
      className={`ds-studies__card ${row.unread ? 'ds-studies__card--unread' : ''}`}
      onClick={() => goToDetail(row._raw)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          goToDetail(row._raw);
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`${tDetail('order')}: ${row.reportId}`}
    >
      <header className="ds-studies__card-head">
        <span className="ds-studies__card-icon" aria-hidden="true">
          <FlaskIcon />
        </span>
        <span className="ds-studies__card-id">{row.reportId}</span>
        <span className="ds-studies__card-date">{row.dateShort}</span>
      </header>

      <div className="ds-studies__card-meta">
        {(row.doctorTitle || row.doctorName) && (
          <span className="ds-studies__card-doctor">
            {row.doctorTitle && (
              <small className="ds-studies__doctor-title">{row.doctorTitle}</small>
            )}
            <span>{row.doctorName}</span>
          </span>
        )}
      </div>

      <footer className="ds-studies__card-foot">
        <span className="ds-studies__card-pills">
          {row.studyType && (
            <Badge variant="neutral" size="sm">
              {row.studyType}
            </Badge>
          )}
          {row.status && (
            <Badge variant={statusVariant(row.statusKey)} size="sm">
              {row.status}
            </Badge>
          )}
        </span>

        <span
          className="ds-studies__card-actions"
          onClick={(e) => e.stopPropagation()}
        >
          {row.newReview && (
            <Tooltip content={t('new-doctor-review') || 'New review'} side="top">
              <span
                className="ds-studies__new-review"
                onClick={(e) => {
                  e.stopPropagation();
                  history.push(`lab-studies-detail/${row.id}/review`);
                }}
              >
                <Icon file="medical-report_TP.svg" width="20px" />
              </span>
            </Tooltip>
          )}
          {row.hasPdf && (
            <Tooltip content="PDF" side="top">
              <span className="ds-studies__icon-btn">
                <PdfLoadingButton
                  reportId={row.id}
                  doctorId={row.doctorId}
                  patientId={row.patientId}
                  prompt={<PdfIcon />}
                  buttonSize="sm"
                />
              </span>
            </Tooltip>
          )}
          <Tooltip content={t('details')} side="top">
            <button
              type="button"
              className="ds-studies__icon-btn ds-studies__icon-btn--primary"
              onClick={(e) => {
                e.stopPropagation();
                goToDetail(row._raw);
              }}
              aria-label={t('details')}
            >
              <ChevronRightIcon />
            </button>
          </Tooltip>
        </span>
      </footer>
    </article>
  );

  return (
    <div className="ds-studies">
      <div className="ds-studies__toolbar">
        <Input
          type="search"
          placeholder={t('search-patient') || tDetail('order')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          iconLeft={<SearchIcon />}
          aria-label={t('search-patient') || tDetail('order')}
          className="ds-studies__search"
        />
        <select
          className="form-control ds-studies__date-filter"
          value={dateMode}
          onChange={(e) => handleDateMode(e.target.value)}
          aria-label={t('all-dates')}
        >
          {DATE_FILTER_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(opt.labelKey)}
            </option>
          ))}
        </select>
        {dateMode === 'personalized' && (
          <div className="ds-studies__date-range">
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              label={t('date-from')}
            />
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              label={t('date-to')}
            />
          </div>
        )}
      </div>

      {loadingStudyList && (!laboratoryList || !laboratoryList.length) ? (
        <LoadingBlock />
      ) : isMobile ? (
        <>
          {mobileRows.length === 0 ? (
            <EmptyState
              title={querySearch ? t('no-search-results') : t('no-studies')}
              description=""
            />
          ) : (
            <div className="ds-studies__cards" role="list">
              {mobileRows.map(renderMobileCard)}
            </div>
          )}
          {showSimplePagination && pageCount > 1 && (
            <nav className="ds-studies__mobile-pager" aria-label="pagination">
              <button
                type="button"
                className="ds-studies__mobile-pager-btn"
                disabled={pageState <= 0}
                onClick={() => dispatch(actionSetPageIndex(pageState - 1))}
                aria-label={ta('prev')}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <span className="ds-studies__mobile-pager-info">
                {pageState + 1} / {pageCount}
              </span>
              <button
                type="button"
                className="ds-studies__mobile-pager-btn"
                disabled={pageState >= lastPageIdx}
                onClick={() => dispatch(actionSetPageIndex(pageState + 1))}
                aria-label={ta('next')}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </nav>
          )}
        </>
      ) : (
        <DataTable
          columns={columns}
          data={data}
          loading={loadingStudyList}
          empty={querySearch ? t('no-search-results') : t('no-studies')}
          manualPagination
          manualSortBy
          onSortChange={() => setSortDate((s) => s * -1)}
          rowClassName={(rowData) =>
            rowData && rowData._unread ? 'ds-studies__row--unread' : ''
          }
          pagination={{
            page: pageState,
            pageSize: pageLimit,
            total: totalEntries,
            lastPage: lastPageIdx,
            onPageChange: (idx) => dispatch(actionSetPageIndex(idx)),
          }}
        />
      )}
    </div>
  );
};

export default StudiesList;
// #ux-redesign-end
